import React, { useCallback, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import styled from 'styled-components';
import { MdSearch } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchManufacturerDistributors } from '../../../../services/Http/ManufacturerApi';
import { useCurrency } from '../../../../context/CurrencyProvider';

const DistributorList = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const navigate = useNavigate();
    const [records, setRecords] = useState([]);
    const [meta, setMeta] = useState({ total: 0, per_page: 10, current_page: 1 });
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    const loadDistributors = useCallback(async ({ page = 1, perPage = 10, searchTerm = '' } = {}) => {
        setLoading(true);
        try {
            const response = await fetchManufacturerDistributors({
                page,
                perPage,
                search: searchTerm,
            });
            setRecords(response.data || []);
            setMeta(response.meta || { total: 0, per_page: perPage, current_page: page });
        } catch (error) {
            toast.error(t('ManufacturerDistributors.errors.loadList'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadDistributors();
    }, [loadDistributors]);

    const handleSearch = (event) => {
        event.preventDefault();
        loadDistributors({ page: 1, perPage: meta.per_page, searchTerm: search });
    };

    const columns = [
        {
            name: t('ManufacturerDistributors.table.columns.distributor'),
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: t('ManufacturerDistributors.table.columns.country'),
            selector: (row) => row.country || t('ManufacturerDistributors.table.notAvailable'),
            sortable: true,
        },
        {
            name: t('ManufacturerDistributors.table.columns.inventory'),
            selector: (row) => row.inventory,
            sortable: true,
        },
        {
            name: t('ManufacturerDistributors.table.columns.sold'),
            selector: (row) => row.sold,
            sortable: true,
        },
        {
            name: t('ManufacturerDistributors.table.columns.inbound'),
            selector: (row) => formatResolvedAmount({
                eurAmount: row.inbound_value,
                localAmount: row.inbound_value_local ?? row.inbound_local,
                localCurrencyId: row.inbound_currency_id ?? row.currency_id,
                localCurrencyCode: row.inbound_currency_code ?? row.currency_code,
            }),
            sortable: true,
        },
        {
            name: t('ManufacturerDistributors.table.columns.outbound'),
            selector: (row) => formatResolvedAmount({
                eurAmount: row.outbound_value,
                localAmount: row.outbound_value_local ?? row.outbound_local,
                localCurrencyId: row.outbound_currency_id ?? row.currency_id,
                localCurrencyCode: row.outbound_currency_code ?? row.currency_code,
            }),
            sortable: true,
        },
        {
            name: t('ManufacturerDistributors.table.columns.venues'),
            selector: (row) => row.venues_count,
            sortable: true,
        },
        {
            name: t('ManufacturerDistributors.table.columns.action'),
            cell: (row) => (
                <ActionButton onClick={() => navigate(`/distributors/${row.id}`)}>
                    {t('ManufacturerDistributors.table.view')}
                </ActionButton>
            ),
        },
    ];

    return (
        <>
            <Toolbar>
                <SearchForm onSubmit={handleSearch}>
                    <MdSearch />
                    <input
                        type="text"
                        placeholder={t('ManufacturerDistributors.table.search')}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </SearchForm>
            </Toolbar>
            <DataTable
                columns={columns}
                data={records}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={meta.total}
                paginationPerPage={meta.per_page}
                onChangePage={(page) => loadDistributors({ page, perPage: meta.per_page, searchTerm: search })}
                onChangeRowsPerPage={(perPage, page) => loadDistributors({ page, perPage, searchTerm: search })}
                highlightOnHover
                persistTableHead
            />
        </>
    );
};

export default DistributorList;

const Toolbar = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
`;

const SearchForm = styled.form`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(130, 22, 146, 0.05);
    border: 1px solid rgba(130, 22, 146, 0.2);
    border-radius: 8px;
    padding: 0.375rem 0.75rem;

    input {
        border: none;
        background: transparent;
        outline: none;
        width: 220px;
    }

    svg {
        color: rgba(130, 22, 146, 1);
    }
`;

const ActionButton = styled.button`
    padding: 0.375rem 0.75rem;
    border-radius: 6px;
    border: 1px solid rgba(130, 22, 146, 0.4);
    background: transparent;
    color: rgba(130, 22, 146, 1);
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: rgba(130, 22, 146, 0.08);
    }
`;
